import { Component, DestroyRef, Input, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, map, of } from 'rxjs';

import { SAVING_ARENA_API_CONFIG } from '../../core/api/api.config';
import { FrontendBlog } from '../../core/api/api.models';
import { blogBodyText, blogReadMinutes } from '../../core/api/blog-text';
import { SavingArenaApiService } from '../../core/api/saving-arena-api.service';
import { BlogPostCard } from '../blog-post-card/blog-post-card';

// One "Insights & Perspectives" card, mapped from a published blog.
interface InsightPost {
  id: string;
  title: string;
  excerpt: string;
  imageUrl: string;
  storeLogoUrl: string;
  category: string;
  categoryPath: string[];
  readTime: string;
  author: string;
  path: string[];
}

interface InsightTab {
  id: string;
  label: string;
}

// Fetch a small buffer over the display cap (guards against any blog dropped in mapping), then
// show only the latest LATEST_INSIGHTS_DISPLAY_LIMIT.
const FETCH_LIMIT = 12;
/**
 * How many posts this section shows. 8 fills two complete rows of the four-across grid; 9 left a
 * single orphan card on its own row.
 *
 * Exported because the deals page's own guides row continues where this one stops — it skips this
 * many posts so the two never show the same article. Change it here and that offset follows.
 */
export const LATEST_INSIGHTS_DISPLAY_LIMIT = 8;
const DISPLAY_LIMIT = LATEST_INSIGHTS_DISPLAY_LIMIT;
const MAX_TABS = 6;

// Shared "Insights & Perspectives" section: shows the latest published blogs with category tabs.
// Drop <app-latest-insights /> onto any page — the .sav-journal-* styles are global.
@Component({
  selector: 'app-latest-insights',
  imports: [BlogPostCard],
  templateUrl: './latest-insights.html',
  styleUrl: './latest-insights.scss',
})
export class LatestInsights implements OnInit {
  private readonly api = inject(SavingArenaApiService);
  private readonly apiConfig = inject(SAVING_ARENA_API_CONFIG);
  private readonly destroyRef = inject(DestroyRef);

  // Optional section copy (defaults keep the shared "Insights & Perspectives" heading).
  @Input() heading = 'Insights & Perspectives';
  @Input() description =
    'Stay updated with the latest industry trends, shopping guides, and deal insights from our expert team.';

  /**
   * Set false to drop the category tab row and show the latest posts straight.
   *
   * Worth knowing before turning it on anywhere: the tabs do not currently filter anything. The
   * template renders `posts()` while `visiblePosts()` — the filtered list — is only read by the
   * empty-state check, so selecting a category highlights the tab and leaves the cards untouched.
   */
  @Input() showTabs = true;

  // When `blogs` is provided (even an empty array), the component renders exactly those blogs in
  // the home-page card design instead of fetching the latest ones itself. Used by the categories
  // page to show admin-selected blogs.
  private usesExternalBlogs = false;

  @Input() set blogs(value: FrontendBlog[] | null | undefined) {
    if (value === undefined) {
      return;
    }
    this.usesExternalBlogs = true;
    const posts = this.toPosts(value ?? []);
    this.posts.set(posts);
    this.tabs.set(this.deriveTabs(posts));
  }

  protected readonly posts = signal<InsightPost[]>([]);
  protected readonly tabs = signal<InsightTab[]>([{ id: 'all', label: 'All' }]);
  protected readonly selectedTabId = signal('all');

  protected readonly visiblePosts = computed(() => {
    const tabId = this.selectedTabId();
    const posts = this.posts();

    if (tabId === 'all') {
      return posts;
    }

    const tab = this.tabs().find((item) => item.id === tabId);
    return tab ? posts.filter((post) => post.category === tab.label) : posts;
  });

  ngOnInit(): void {
    // Caller supplied its own blogs — don't fetch the latest.
    if (this.usesExternalBlogs) {
      return;
    }

    this.api
      .getBlogs({ page: 1, limit: FETCH_LIMIT, fields: 'card' })
      .pipe(
        map((response) => this.toPosts(response.data)),
        catchError(() => of<InsightPost[]>([])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((posts) => {
        this.posts.set(posts);
        this.tabs.set(this.deriveTabs(posts));
      });
  }

  protected selectTab(id: string): void {
    this.selectedTabId.set(id);
  }

  private toPosts(blogs: FrontendBlog[]): InsightPost[] {
    return blogs
      .map((blog) => ({ blog, timestamp: this.timestamp(blog) }))
      .sort((a, b) => b.timestamp - a.timestamp)
      .map(({ blog }) => this.toPost(blog))
      .filter((post): post is InsightPost => post !== null)
      .slice(0, DISPLAY_LIMIT);
  }

  private toPost(blog: FrontendBlog): InsightPost | null {
    const title = this.clean(blog.name);
    const slug = this.clean(blog.slug);

    if (!title || !slug) {
      return null;
    }

    const category = this.clean(blog.category?.name ?? '') || 'Shopping Tips';
    const categorySlug = this.clean(blog.category?.slug ?? '') || this.slugify(category);
    const store = Array.isArray(blog.stores) ? blog.stores[0] : undefined;
    const storeLogo =
      store && typeof store.logoImagePath === 'string' ? store.logoImagePath.trim() : '';

    return {
      id: blog.id || slug,
      title,
      excerpt: this.excerpt(blog),
      imageUrl: blog.thumbnailImagePath
        ? this.assetUrl(blog.thumbnailImagePath)
        : '/images/blog-placeholder.svg',
      storeLogoUrl: storeLogo ? this.assetUrl(storeLogo) : '',
      category,
      categoryPath: ['/blogs', categorySlug],
      readTime: `${this.readMinutes(blog)} Min`,
      author: this.authorName(blog),
      path: ['/blogs', slug],
    };
  }

  private deriveTabs(posts: InsightPost[]): InsightTab[] {
    const seen = new Set<string>();
    const tabs: InsightTab[] = [{ id: 'all', label: 'All' }];

    for (const post of posts) {
      const key = post.category.toLowerCase();
      if (post.category && !seen.has(key)) {
        seen.add(key);
        tabs.push({ id: this.slugify(post.category), label: post.category });
      }
    }

    return tabs.slice(0, MAX_TABS);
  }

  private authorName(blog: FrontendBlog): string {
    const raw = blog['author'];
    if (typeof raw === 'string') {
      return this.clean(raw) || 'SavingArena Team';
    }
    if (raw && typeof raw === 'object' && typeof (raw as { name?: unknown }).name === 'string') {
      return this.clean((raw as { name: string }).name) || 'SavingArena Team';
    }
    return 'SavingArena Team';
  }

  private excerpt(blog: FrontendBlog): string {
    const shortDescription = this.clean(blog.shortDescription ?? '');
    if (shortDescription) {
      return shortDescription;
    }

    const content = this.clean(blogBodyText(blog));
    if (!content) {
      return this.clean(blog.name);
    }

    return content.length <= 150 ? content : `${content.slice(0, 150).trimEnd()}...`;
  }

  private readMinutes(blog: FrontendBlog): number {
    return blogReadMinutes(blog);
  }

  private timestamp(blog: FrontendBlog): number {
    const value = blog.publishAt || blog.updatedAt;
    if (!value) {
      return 0;
    }
    const ms = new Date(value).getTime();
    return Number.isNaN(ms) ? 0 : ms;
  }

  private clean(value: string): string {
    return String(value ?? '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private slugify(value: string): string {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  private assetUrl(path: string): string {
    if (/^https?:\/\//i.test(path)) {
      return path;
    }
    const base = this.apiConfig.assetBaseUrl.replace(/\/$/, '');
    return `${base}${path.startsWith('/') ? path : `/${path}`}`;
  }
}
